import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { pairwise, tap } from "rxjs/operators";

import { SessionStorageService } from "../session-storage.service";
import { AvailabilityActions, DateOfStayActions, LayoutActions, OrderActions } from "../actions";
import { Availability } from "src/objects/availability";
import { LayoutPosition } from "src/objects/layout-unit-position";
import { parseISO, isEqual } from "date-fns";
import { DateOfStay } from "src/objects/date-of-stay";
import { Destination } from "src/objects/destination";
import { Order } from "src/objects/order";
import { Product } from "src/objects/product";
import { Store } from "@ngrx/store";





export interface SelectedAvailabilitiesCacheRepresentation {
    orderToken: string;
    productId: number;
    destinationId: number;
    startDate: string;
    endDate: string;
    availabilities: Availability[];
}

export interface SelectedLayoutUnitPositionsCacheRepresentation {
    orderToken: string;
    productId: number;
    destinationId: number;
    startDate: string;
    endDate: string;
    layoutUnitPositions: LayoutPosition[];
}

export const validateCachedAvailabilities = (args: {
    cachedAvailabilities: SelectedAvailabilitiesCacheRepresentation, 
    remoteAvailabilities: Availability[],
    order: Order,
    product: Product,
    destination: Destination,
    dateOfStay: DateOfStay
}): Availability[] => {
    const { cachedAvailabilities, remoteAvailabilities, order, product, destination, dateOfStay } = args;
    const { orderToken, productId, destinationId, startDate, endDate, availabilities } = cachedAvailabilities;
          
    if (orderToken === order.token &&
        productId === product.id &&
        destinationId === destination.id &&
        isEqual(parseISO(startDate), dateOfStay.start!) &&
        isEqual(parseISO(endDate), dateOfStay.end!)) {
    
        if (availabilities.length > 0) {
            const allCachedAvailabilitiesAreValid = availabilities
                .every(cachedAvailability => remoteAvailabilities.some(validAvailability => validAvailability.id === cachedAvailability.id));
            
            if (allCachedAvailabilitiesAreValid) {
                return availabilities;
            }
        }
    }

    return [];
}

export const validateCachedLayoutUnitPositions = (args: {
    cachedLayoutUnitPositions: SelectedLayoutUnitPositionsCacheRepresentation, 
    remoteLayoutUnitPositions: LayoutPosition[],
    order: Order,
    product: Product,
    destination: Destination,
    dateOfStay: DateOfStay
}): LayoutPosition[] => {
    const { cachedLayoutUnitPositions, remoteLayoutUnitPositions, order, product, destination, dateOfStay } = args;
    const { orderToken, productId, destinationId, startDate, endDate, layoutUnitPositions } = cachedLayoutUnitPositions;

    if (orderToken === order.token &&
        productId === product.id &&
        destinationId === destination.id &&
        isEqual(parseISO(startDate), dateOfStay.start!) &&
        isEqual(parseISO(endDate), dateOfStay.end!)) {
        
        if (layoutUnitPositions.length > 0) {
            const allCachedLayoutUnitPositionsAreValid = layoutUnitPositions
                .every(cachedLayoutUnitPosition => remoteLayoutUnitPositions.some(validLayoutUnitPosition => validLayoutUnitPosition.id === cachedLayoutUnitPosition.id));
            
            if (allCachedLayoutUnitPositionsAreValid) {
                return layoutUnitPositions;
            }
        }
    }


    return [];
}

@Injectable()
export class SessionStorageEffects {
    private sessionStorageService = inject(SessionStorageService);
    private actions$ = inject(Actions);
    private store = inject(Store);

    selectedAvailabilitiesStoreSideEffect$ = createEffect(() => this.actions$
        .pipe(
        ofType(AvailabilityActions.setSelectedAvailabilities),
            tap(({ availabilities, product, destination, dateOfStay, order }) => {
                const storedAvailabilities = {
                    orderToken: order.token,
                    productId: product.id,
                    destinationId: destination.id,
                    startDate: dateOfStay.start?.toISOString(),
                    endDate: dateOfStay.end?.toISOString(),
                    availabilities,
                } as SelectedAvailabilitiesCacheRepresentation;
                

                if (!!availabilities && availabilities.length > 0) {
                    this.sessionStorageService.setItem('selectedAvailabilities', storedAvailabilities);
                } else {
                    this.sessionStorageService.removeItem('selectedAvailabilities');
                }
            }),
        ),
        { dispatch: false },
    );

    selectLayoutUnitPositionSideEffect$ = createEffect(() => this.actions$
        .pipe(
            ofType(LayoutActions.selectLayoutUnitPositions),
            tap(({ layoutUnitPositions, product, destination, dateOfStay, order }) => {
                const storedLayoutUnitPositions = {
                    orderToken: order.token,
                    productId: product.id,
                    destinationId: destination.id,
                    startDate: dateOfStay.start?.toISOString(),
                    endDate: dateOfStay.end?.toISOString(),
                    layoutUnitPositions,
                } as SelectedLayoutUnitPositionsCacheRepresentation;

                if (!!layoutUnitPositions && layoutUnitPositions.length > 0) {
                    this.sessionStorageService.setItem('selectedLayoutUnitPositions', storedLayoutUnitPositions);
                } else {
                    this.sessionStorageService.removeItem('selectedLayoutUnitPositions');
                }
            }),
        ),
        { dispatch: false },
    );

    clearOnDateOfStayChange$ = createEffect(() => this.actions$
        .pipe(
            ofType(DateOfStayActions.setDateOfStay),
            pairwise(),
            tap(([{ dateOfStay: previousDateOfStay }, { dateOfStay: newDateOfStay }]) => {
                const startDateChanged = !isEqual(previousDateOfStay?.start ??  new Date(), newDateOfStay?.start ?? new Date());
                const endDateChanged = !isEqual(previousDateOfStay?.end ??  new Date(), newDateOfStay?.end ?? new Date());
                if (startDateChanged || endDateChanged) {
                    this.sessionStorageService.removeItem('selectedAvailabilities');
                    this.sessionStorageService.removeItem('selectedLayoutUnitPositions');
                }
            }),
        ),
        { dispatch: false },
    );
    /**
     * Clear selected availabilities from session storage when an item is added to the order
     */
    onItemAddedClearSelectedAvailabilitiesSessionStorage$ = createEffect(() => this.actions$
        .pipe(
            ofType(OrderActions.addItemSuccess),
            tap(() => {
                this.sessionStorageService.removeItem('selectedAvailabilities');
                this.sessionStorageService.removeItem('selectedLayoutUnitPositions');
            }),
        ),
        { dispatch: false },
    );

        /**
         * Clear session storage when the order is cleared
         */
    onOrderClearedClearSessionStorage$ = createEffect(() => this.actions$
        .pipe(
            ofType(OrderActions.clearOrder, OrderActions.createOrder),
            tap(() => {
                this.sessionStorageService.clear();
            }),
        ),
        { dispatch: false },
    );
}